import '@mui/material/styles';
import '@mui/material/Typography';

import {
  Breakpoints,
  FontFamily,
  PrimaryColors,
} from '@/common/theme/constants';

import { CSSProperties } from '@mui/material';
import { TypographyVariantsOptions } from '@mui/material/styles';

/** According to: https://www.figma.com/design/ihf0xZsQpZoxcJIqgmJ1G2/UGG---Design-External-(1)?node-id=8541-7099&node-type=text&m=dev */

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body3: CSSProperties;
    body4: CSSProperties;
    body5: CSSProperties;
    h2large: CSSProperties;
    h7: CSSProperties;
    h8: CSSProperties;
    h9: CSSProperties;
    h10: CSSProperties;
    priceL: CSSProperties;
    priceM: CSSProperties;
    priceS: CSSProperties;
    footerLinks: CSSProperties;
    label: CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: CSSProperties;
    body4?: CSSProperties;
    body5?: CSSProperties;
    h2large?: CSSProperties;
    h7?: CSSProperties;
    h8?: CSSProperties;
    h9?: CSSProperties;
    h10?: CSSProperties;
    priceL?: CSSProperties;
    priceM?: CSSProperties;
    priceS?: CSSProperties;
    footerLinks?: CSSProperties;
    label?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
    body4: true;
    body5: true;
    h2large: true;
    h7: true;
    h8: true;
    h9: true;
    h10: true;
    priceL: true;
    priceM: true;
    priceS: true;
    footerLinks: true;
    label: true;
  }
}

// Define a type for CSS properties with media queries
type CSSPropertiesWithMediaQueries = CSSProperties & {
  [key: string]: string | number | CSSProperties | undefined;
};

export const typography: TypographyVariantsOptions = {
  fontFamily: FontFamily.body,
  h1: {
    fontFamily: FontFamily.heading,
    fontSize: '40px',
    lineHeight: '33px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '120px',
      lineHeight: '100px',
    },
  } as CSSPropertiesWithMediaQueries,
  h2: {
    fontFamily: FontFamily.heading,
    fontSize: '32px',
    lineHeight: '27px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '72px',
      lineHeight: '60px',
    },
  } as CSSPropertiesWithMediaQueries,
  h2large: {
    fontFamily: FontFamily.heading,
    fontSize: '32px',
    lineHeight: '27px',
    textTransform: 'uppercase',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '110px',
      lineHeight: '100px',
    },
  } as CSSPropertiesWithMediaQueries,
  h3: {
    fontFamily: FontFamily.heading,
    fontSize: '24px',
    lineHeight: 'normal',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '40px',
    },
  } as CSSPropertiesWithMediaQueries,
  h4: {
    fontFamily: FontFamily.heading,
    fontSize: '20px',
    lineHeight: 'normal',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '28px',
    },
  } as CSSPropertiesWithMediaQueries,
  h5: {
    fontFamily: FontFamily.heading,
    fontSize: '16px',
    lineHeight: 'normal',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '24px',
    },
  } as CSSPropertiesWithMediaQueries,
  h6: {
    fontFamily: FontFamily.heading,
    fontSize: '12px',
    lineHeight: 'normal',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '24px',
    },
  } as CSSPropertiesWithMediaQueries,
  h7: {
    fontFamily: FontFamily.heading,
    fontSize: '20px',
    lineHeight: '30px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '24px',
      lineHeight: '21px',
    },
  } as CSSPropertiesWithMediaQueries,
  h8: {
    fontFamily: FontFamily.body,
    textTransform: 'uppercase',
    fontSize: '16px',
    lineHeight: '16px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '32px',
      lineHeight: '21px',
    },
  } as CSSPropertiesWithMediaQueries,
  h9: {
    fontFamily: FontFamily.body,
    textTransform: 'uppercase',
    fontSize: '14px',
    lineHeight: '14px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '24px',
      lineHeight: '24px',
    },
  } as CSSPropertiesWithMediaQueries,
  h10: {
    fontFamily: FontFamily.body,
    textTransform: 'uppercase',
    fontSize: '16px',
    lineHeight: '20px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '20px',
      lineHeight: '20px',
    },
  } as CSSPropertiesWithMediaQueries,
  body1: {
    fontSize: '14px',
    lineHeight: '20px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '16px',
      lineHeight: '24px',
    },
  } as CSSPropertiesWithMediaQueries,
  body2: {
    fontSize: '18px',
    lineHeight: '26px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '24px',
      lineHeight: '37px',
    },
  } as CSSPropertiesWithMediaQueries,
  body3: {
    fontSize: '14px',
    lineHeight: '20px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '20px',
      lineHeight: '26px',
    },
  } as CSSPropertiesWithMediaQueries,
  body4: {
    fontSize: '12px',
    lineHeight: '19px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '14px',
      lineHeight: '20px',
    },
  } as CSSPropertiesWithMediaQueries,
  body5: {
    fontSize: '10px',
    lineHeight: '15px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '12px',
      lineHeight: '19px',
    },
  } as CSSPropertiesWithMediaQueries,
  priceM: {
    fontSize: '16px',
    lineHeight: '24px',
    fontFamily: FontFamily.heading,
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '28px',
      lineHeight: '28px',
      fontFamily: FontFamily.heading,
    },
  },
  priceL: {
    fontSize: '34px',
    lineHeight: '34px',
    fontFamily: FontFamily.heading,
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '38px',
      lineHeight: '38px',
    },
  } as CSSPropertiesWithMediaQueries,
  priceS: {
    fontSize: '15px',
    lineHeight: '24px',
    fontFamily: FontFamily.heading,
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '20px',
      lineHeight: '24px',
    },
  } as CSSPropertiesWithMediaQueries,
  footerLinks: {
    fontSize: '16px',
    lineHeight: '40px',
    [`@media screen and (min-width: ${Breakpoints.md}px)`]: {
      fontSize: '16px',
      lineHeight: '36px',
    },
  } as CSSPropertiesWithMediaQueries,
  label: {
    textTransform: 'uppercase',
    color: PrimaryColors.radical,
    fontSize: '10px',
    lineHeight: '15px',
    fontWeight: 700,
  },
};
